import { Logo } from "@/components/rac/logo";
import { useAuth } from "@/hooks/use-auth";
import { useUserbackFeedback } from "@/hooks/use-userback-feedback";
import { useVersionCheck } from "@/hooks/use-version-check";
import { COMPANY_INFO } from "@/info/company";
import { VERSION } from "@/lib/version";
import { trackContactCall, trackContactMail } from "@/utils/analytics/track-contact";
import { trackEvent } from "@/utils/analytics/track-event";
import type { PropertyLink } from "@/utils/footer-links";
import { Loader2, Mail, MapPin, Phone } from "lucide-react";
import Link from "next/link";
import { type ReactNode } from "react";
import { Button } from "../rac/button";
import { SocialLinks } from "./social-links";

const companyLinks = [
	{ label: "About us", href: "/about" },
	{ label: "Blog", href: `${COMPANY_INFO.website}/blog` },
	{ label: "Careers", href: `mailto:${COMPANY_INFO.contact.email.careers}` },
	{ label: "Contact", href: "/contact" },
	{ label: "Settlin Reviews", href: "/reviews" },
	{ label: "FAQ", href: "/faq" },
	{ label: "Pricing", href: "/pricing" },
	{ label: "Privacy Policy", href: "/privacy-policy" },
	{ label: "Sitemap", href: "/sitemap" },
	{ label: "Terms of use", href: "/terms" },
	{ label: "Features", href: "/features" },
];

interface FooterClientProps {
	linkColumns: PropertyLink[][];
	seoContent?: ReactNode;
}

export function FooterClient({ linkColumns, seoContent }: FooterClientProps) {
	const currentYear = new Date().getFullYear();
	const { user } = useAuth();
	const { hasUpdate, serverVersion, reloadToUpdate } = useVersionCheck(300000);
	const { openFeedback: handleFeedbackClick, isLoading: isFeedbackLoading } = useUserbackFeedback();

	const handleSocialClick = (platform: string, url: string) => {
		// Track social media clicks
		trackEvent({
			facebook: {
				event: "Contact",
				options: {
					content_name: `Social Media - ${platform}`,
					content_category: "social_media",
				},
			},
			google: {
				event: "select_content",
				options: {
					content_type: "social_media",
					item_id: platform.toLowerCase(),
					custom_parameters: { destination_url: url },
				},
			},
			matomo: {
				event: `click on ${platform.toLowerCase()}`,
				options: {
					category: "contact",
				},
			},
		});
	};

	const handleQuickLinkClick = (linkLabel: string, linkHref: string) => {
		// Track quick link navigation
		trackEvent({
			google: {
				event: "select_content",
				options: {
					content_type: "quick_link",
					item_id: linkLabel.toLowerCase().replace(/\s+/g, "-"),
					custom_parameters: { destination_url: linkHref },
				},
			},
			matomo: {
				event: "quick-link-click",
				options: {
					category: "navigation",
				},
			},
		});
	};

	const handleCompanyLinkClick = (linkLabel: string, linkHref: string) => {
		// Track company link navigation
		trackEvent({
			google: {
				event: "select_content",
				options: {
					content_type: "company_link",
					item_id: linkLabel.toLowerCase().replace(/\s+/g, "-"),
					custom_parameters: { destination_url: linkHref },
				},
			},
			matomo: {
				event: "company-link-click",
				options: {
					category: "navigation",
				},
			},
		});
	};

	const handleContactClick = (contactType: string) => {
		// Track contact interactions with specific functions
		if (contactType.toLowerCase() === "email") {
			trackContactMail();
		} else if (contactType.toLowerCase() === "phone") {
			trackContactCall();
		}

		// Keep original tracking for other contact types
		trackEvent({
			facebook: {
				event: "Contact",
				options: {
					content_name: `Contact - ${contactType}`,
					content_category: "contact_info",
				},
			},
			google: {
				event: "select_content",
				options: {
					content_type: "contact",
					item_id: contactType.toLowerCase(),
				},
			},
			matomo: {
				event: `click on ${contactType.toLowerCase()}`,
				options: {
					category: "contact",
				},
			},
		});
	};

	return (
		<footer className="w-full">
			{/* SEO Content Section */}
			{seoContent && <section className="bg-gradient-to-r from-[#251821] to-[#31202C]">{seoContent}</section>}

			{/* Quick Links Section */}
			<section className="bg-[#251821] py-6 md:py-12">
				<div className="container mx-auto px-4">
					<h2 className="text-2xl md:text-3xl font-bold text-white text-center underline mb-6 md:mb-8">Quick links</h2>

					<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
						{linkColumns.map((columnLinks, columnIndex) => (
							<div key={columnIndex} className="flex flex-col gap-2">
								{columnLinks.map((link, index) => (
									<Link
										key={index}
										href={link.href}
										aria-label={`View properties: ${link.label}`}
										className="text-sm font-bold text-white hover:underline transition-colors"
										onClick={() => handleQuickLinkClick(link.label, link.href)}
									>
										{link.label}
									</Link>
								))}
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Main Footer */}
			<section className="bg-[#31202C] pt-6 md:pt-12 pb-3 md:pb-6">
				<div className="container mx-auto px-4">
					<div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
						{/* Company Info */}
						<div className="space-y-4">
							<Logo withName className="h-30 w-auto text-white" />
							<p className="text-gray-300 text-sm">Your trusted partner in property transactions in Bangalore.</p>
							<SocialLinks handleSocialClick={handleSocialClick} />
						</div>

						{/* Company Links */}
						<div>
							<div className="text-lg font-semibold text-white mb-4">Company</div>
							<ul className="space-y-2">
								{companyLinks.map(link => (
									<li key={link.href}>
										<Link
											href={link.href}
											aria-label={`Go to ${link.label}`}
											className="text-gray-300 hover:text-white text-sm transition-colors"
											onClick={() => handleCompanyLinkClick(link.label, link.href)}
										>
											{link.label}
										</Link>
									</li>
								))}
								<li>
									<Button
										variant="bareshell"
										className="p-0 m-0 h-auto text-gray-300 hover:text-white text-sm transition-colors flex items-center gap-2"
										onClick={handleFeedbackClick}
										isDisabled={isFeedbackLoading}
									>
										{isFeedbackLoading && <Loader2 className="w-3 h-3 animate-spin" />}
										Feedback
									</Button>
								</li>
							</ul>
						</div>

						{/* Contact Info */}
						<div>
							<div className="text-lg font-semibold text-white mb-4">Contact Us</div>
							<div className="space-y-2 text-sm text-gray-300">
								<p className="flex items-start gap-2">
									<Mail className="h-5 w-5 shrink-0 mt-0.5" />
									<a
										target="_blank"
										rel="noreferer noopener"
										href={`mailto:${COMPANY_INFO.contact.email.primary}`}
										onClick={() => handleContactClick("Email")}
										className="hover:text-white transition-colors"
									>
										{COMPANY_INFO.contact.email.primary}
									</a>
								</p>
								<p className="flex items-start gap-2">
									<Phone className="h-5 w-5 shrink-0 mt-0.5" />
									<a
										target="_blank"
										rel="noreferer noopener"
										href={`tel:${COMPANY_INFO.contact.phone.primary.replace(/\s|-/g, "")}`}
										onClick={() => handleContactClick("Phone")}
										className="hover:text-white transition-colors"
									>
										{COMPANY_INFO.contact.phone.primary}
									</a>
								</p>
								<p className="flex items-start gap-2">
									<MapPin className="h-5 w-5 shrink-0 mt-0.5" />
									<a
										target="_blank"
										rel="noreferer noopener"
										href={COMPANY_INFO.address.googleMapsUrl}
										onClick={() => handleContactClick("Address")}
										className="hover:text-white transition-colors"
									>
										{COMPANY_INFO.address.full}
									</a>
								</p>
							</div>
						</div>
					</div>

					{/* Copyright */}
					<div className="border-t border-gray-700 pt-6 mt-8">
						<div className="flex flex-col md:flex-row justify-between items-center">
							<div className="flex items-center gap-2" aria-label="Settlin Logo">
								<Logo className="h-5 w-auto text-white" />
								<span className="text-sm text-gray-400">
									{currentYear} {COMPANY_INFO.legalName}. All rights reserved. v{VERSION}
								</span>
								{hasUpdate && (
									<button
										onClick={reloadToUpdate}
										className="text-sm text-primary-foreground bg-primary/80 hover:bg-primary px-2 py-0.5 rounded transition-colors"
									>
										Update to v{serverVersion}
									</button>
								)}
							</div>
							<div className="flex space-x-6 mt-4 md:mt-0">
								<Link
									href="/terms"
									aria-label="Read our Terms of Service"
									className="text-sm text-gray-400 hover:text-white"
									onClick={() => handleCompanyLinkClick("Terms of Service", "/terms")}
								>
									Terms of Service
								</Link>
								<Link
									href="/privacy-policy"
									aria-label="Read our Privacy Policy"
									className="text-sm text-gray-400 hover:text-white"
									onClick={() => handleCompanyLinkClick("Privacy Policy", "/privacy-policy")}
								>
									Privacy Policy
								</Link>
							</div>
						</div>
					</div>
				</div>
			</section>
		</footer>
	);
}
